import { type FormEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import {
  type LivreDetail,
  type Personnage,
  type PersonnageEcrit,
  personnageEcritSchema,
} from "./contract";
import { BOUTON_CONTOUR, CHAMP } from "./histoires-commun";
import { messageDEchec, useEcrirePersonnage } from "./histoires-requetes";

/**
 * L'onglet Personnages d'un livre (#226) : la liste, et le formulaire d'un personnage neuf ou
 * modifie. Pas encore d'image : les planches arrivent avec les tickets des images. Aucune
 * maquette ne dessine cet onglet ; il reprend la carte et les champs des autres.
 */

type Edition =
  | { readonly type: "aucune" }
  | { readonly type: "nouveau" }
  | { readonly type: "modifier"; readonly id: number };

export function OngletPersonnages({ detail }: { detail: LivreDetail }) {
  const { personnages } = detail;
  const [edition, setEdition] = useState<Edition>({ type: "aucune" });
  const ajouter = useRef<HTMLButtonElement>(null);
  const [retour, setRetour] = useState<number | "ajouter" | null>(null);

  // Sortie d'un formulaire : le focus revient au bouton qui l'a ouvert.
  useEffect(() => {
    if (retour === null) return;
    if (retour === "ajouter") ajouter.current?.focus();
    else document.getElementById(`histoires-modifier-personnage-${retour}`)?.focus();
    setRetour(null);
  }, [retour]);

  const fermer = (): void => {
    setRetour(edition.type === "modifier" ? edition.id : "ajouter");
    setEdition({ type: "aucune" });
  };

  return (
    <section aria-labelledby="histoires-personnages" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold" id="histoires-personnages">
            Personnages
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            La description et les signes s'écrivent en anglais : ils partent dans les prompts des
            images, les signes redits à chaque référence. Les planches arrivent avec les images.
          </p>
        </div>
        <button
          className={BOUTON_CONTOUR}
          disabled={edition.type !== "aucune"}
          onClick={() => setEdition({ type: "nouveau" })}
          ref={ajouter}
          type="button"
        >
          Ajouter un personnage
        </button>
      </div>
      {personnages.length === 0 && edition.type !== "nouveau" ? (
        <p className="text-sm text-muted-foreground">Aucun personnage pour l'instant.</p>
      ) : null}
      {personnages.length === 0 ? null : (
        <ul aria-label="Personnages du livre" className="grid items-start gap-4 lg:grid-cols-2">
          {personnages.map((personnage) => (
            <li className="min-w-0" key={personnage.id}>
              {edition.type === "modifier" && edition.id === personnage.id ? (
                <FormulairePersonnage
                  fermer={fermer}
                  livreId={detail.livre.id}
                  personnage={personnage}
                />
              ) : (
                <CartePersonnage
                  modifiable={edition.type === "aucune"}
                  modifier={() => setEdition({ type: "modifier", id: personnage.id })}
                  personnage={personnage}
                />
              )}
            </li>
          ))}
        </ul>
      )}
      {edition.type === "nouveau" ? (
        <FormulairePersonnage fermer={fermer} livreId={detail.livre.id} personnage={null} />
      ) : null}
    </section>
  );
}

function Ligne({
  nom,
  children,
  anglais = false,
}: {
  nom: string;
  children: string | null;
  anglais?: boolean;
}) {
  if (children === null || children.trim() === "") return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-semibold text-muted-foreground">{nom}</dt>
      <dd className="whitespace-pre-line text-sm" lang={anglais ? "en" : undefined}>
        {children}
      </dd>
    </div>
  );
}

function CartePersonnage({
  personnage,
  modifier,
  modifiable,
}: {
  personnage: Personnage;
  modifier: () => void;
  modifiable: boolean;
}) {
  return (
    <article className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold">
          {personnage.nom}
          {personnage.espece === null || personnage.espece.trim() === "" ? null : (
            <span className="ml-2 text-sm font-normal text-muted-foreground">
              {personnage.espece}
            </span>
          )}
        </h3>
        <button
          aria-label={`Modifier ${personnage.nom}`}
          className={cn(BOUTON_CONTOUR, "min-h-8 px-3 py-1")}
          disabled={!modifiable}
          id={`histoires-modifier-personnage-${personnage.id}`}
          onClick={modifier}
          type="button"
        >
          Modifier
        </button>
      </div>
      <dl className="flex flex-col gap-2">
        <Ligne anglais nom="Description">
          {personnage.description}
        </Ligne>
        <Ligne anglais nom="Signes distinctifs">
          {personnage.signes}
        </Ligne>
        <Ligne nom="Notes">{personnage.notes}</Ligne>
      </dl>
    </article>
  );
}

/** Un champ vide ne dit rien : il part en `null`, comme le contrat l'attend. */
function ouNull(valeur: string): string | null {
  return valeur.trim() === "" ? null : valeur;
}

/** La saisie mise a la forme du contrat, ou la raison du refus. */
export function personnageDepuisSaisie(saisie: {
  readonly nom: string;
  readonly espece: string;
  readonly description: string;
  readonly signes: string;
  readonly notes: string;
}):
  | { readonly ok: true; readonly personnage: PersonnageEcrit }
  | { readonly ok: false; readonly refus: string } {
  if (saisie.nom.trim() === "") return { ok: false, refus: "Le nom est vide." };
  const lu = personnageEcritSchema.safeParse({
    nom: saisie.nom,
    espece: ouNull(saisie.espece)?.trim() ?? null,
    description: ouNull(saisie.description),
    signes: ouNull(saisie.signes),
    notes: ouNull(saisie.notes),
  });
  if (lu.success) return { ok: true, personnage: lu.data };
  return { ok: false, refus: "Un champ est trop long pour le contrat." };
}

function FormulairePersonnage({
  livreId,
  personnage,
  fermer,
}: {
  livreId: number;
  personnage: Personnage | null;
  fermer: () => void;
}) {
  const ecrire = useEcrirePersonnage(livreId);
  const [nom, setNom] = useState(personnage?.nom ?? "");
  const [espece, setEspece] = useState(personnage?.espece ?? "");
  const [description, setDescription] = useState(personnage?.description ?? "");
  const [signes, setSignes] = useState(personnage?.signes ?? "");
  const [notes, setNotes] = useState(personnage?.notes ?? "");
  const [refus, setRefus] = useState<string | null>(null);
  const champNom = useRef<HTMLInputElement>(null);
  const id = useId();

  useEffect(() => {
    champNom.current?.focus();
  }, []);

  const envoyer = (evenement: FormEvent): void => {
    evenement.preventDefault();
    if (ecrire.isPending) return;
    const lu = personnageDepuisSaisie({ nom, espece, description, signes, notes });
    if (!lu.ok) {
      setRefus(lu.refus);
      return;
    }
    setRefus(null);
    ecrire.mutate({ id: personnage?.id, personnage: lu.personnage }, { onSuccess: fermer });
  };

  const titre = personnage === null ? "Nouveau personnage" : `Modifier ${personnage.nom}`;

  return (
    <form
      aria-label={titre}
      className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4 text-card-foreground"
      noValidate
      onSubmit={envoyer}
    >
      <h3 className="text-base font-semibold">{titre}</h3>
      <div className="grid gap-3 sm:grid-cols-2">
        <Saisie etiquette="Nom" id={`${id}-nom`}>
          <input
            className={CHAMP}
            id={`${id}-nom`}
            maxLength={80}
            onChange={(evenement) => setNom(evenement.target.value)}
            ref={champNom}
            value={nom}
          />
        </Saisie>
        <Saisie etiquette="Espèce" id={`${id}-espece`}>
          <input
            className={CHAMP}
            id={`${id}-espece`}
            maxLength={80}
            onChange={(evenement) => setEspece(evenement.target.value)}
            placeholder="loutre"
            value={espece}
          />
        </Saisie>
      </div>
      <Saisie etiquette="Description, en anglais" id={`${id}-description`}>
        <textarea
          className={cn(CHAMP, "min-h-20")}
          id={`${id}-description`}
          lang="en"
          maxLength={2000}
          onChange={(evenement) => setDescription(evenement.target.value)}
          value={description}
        />
      </Saisie>
      <Saisie etiquette="Signes distinctifs, en anglais" id={`${id}-signes`}>
        <textarea
          className={cn(CHAMP, "min-h-16")}
          id={`${id}-signes`}
          lang="en"
          maxLength={1000}
          onChange={(evenement) => setSignes(evenement.target.value)}
          value={signes}
        />
      </Saisie>
      <Saisie etiquette="Notes" id={`${id}-notes`}>
        <textarea
          className={cn(CHAMP, "min-h-16")}
          id={`${id}-notes`}
          maxLength={2000}
          onChange={(evenement) => setNotes(evenement.target.value)}
          value={notes}
        />
      </Saisie>
      {refus === null ? null : (
        <p className="text-sm text-destructive" role="alert">
          {refus}
        </p>
      )}
      {ecrire.isError ? (
        <p className="text-sm text-destructive" role="alert">
          {messageDEchec(ecrire.error)}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Button
          aria-disabled={ecrire.isPending || undefined}
          className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
          type="submit"
        >
          Enregistrer
        </Button>
        <button className={BOUTON_CONTOUR} onClick={fermer} type="button">
          Annuler
        </button>
      </div>
    </form>
  );
}

function Saisie({
  etiquette,
  id,
  children,
}: {
  etiquette: string;
  id: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-semibold" htmlFor={id}>
        {etiquette}
      </label>
      {children}
    </div>
  );
}
